import React, { useEffect, useState } from "react";
import html2canvas from "html2canvas";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Tabs,
  Tab,
  Grid,
  IconButton,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import {
  SaveAlt as SaveAltIcon,
  DateRange as DateRangeIcon,
  School as SchoolIcon,
  Visibility as VisibilityIcon,
  ContentCopy as ContentCopyIcon,
  Restore as RestoreIcon,
  DeleteSweep as DeleteSweepIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import backgroundImage from "../../assets/layout2027.png";

const LAST_AGENDA_STORAGE_KEY = "autentico:last-graph-diary";

const createEmptyEntries = () => [
  {
    aula: "1ª AULA",
    disciplina: "",
    conteudo: "",
    atividade: "",
    casa: "",
  },
  {
    aula: "2ª AULA",
    disciplina: "",
    conteudo: "",
    atividade: "",
    casa: "",
  },
  {
    aula: "3ª AULA",
    disciplina: "",
    conteudo: "",
    atividade: "",
    casa: "",
  },
  {
    aula: "4ª AULA",
    disciplina: "",
    conteudo: "",
    atividade: "",
    casa: "",
  },
  {
    aula: "5ª AULA",
    disciplina: "",
    conteudo: "",
    atividade: "",
    casa: "",
  },
  {
    aula: "6ª AULA",
    disciplina: "",
    conteudo: "",
    atividade: "",
    casa: "",
  },
];

const normalizeEntries = (entries) => {
  const emptyEntries = createEmptyEntries();
  if (!Array.isArray(entries)) return emptyEntries;
  return emptyEntries.map((emptyEntry, index) => {
    const entry = entries[index] || {};
    return {
      ...emptyEntry,
      disciplina: entry.disciplina || "",
      conteudo: entry.conteudo || "",
      atividade: entry.atividade || "",
      casa: entry.casa || "",
    };
  });
};

const hasFilledEntry = (entries) => entries.some((entry) =>
  entry.disciplina || entry.conteudo || entry.atividade || entry.casa
);

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
}));

const StyledTab = styled(Tab)({
  minWidth: "60px",
  flex: 1,
  minHeight: "48px",
  padding: "6px 12px",
});

const AgendaForm = ({ onImageGenerate }) => {
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const [agendaData, setAgendaData] = useState({
    date: getCurrentDate(),
    entries: createEmptyEntries(),
  });

  const [activeTab, setActiveTab] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [hasLastAgenda, setHasLastAgenda] = useState(false);

  useEffect(() => {
    try {
      const savedAgenda = localStorage.getItem(LAST_AGENDA_STORAGE_KEY);
      setHasLastAgenda(Boolean(savedAgenda));
    } catch (error) {
      setHasLastAgenda(false);
    }
  }, []);

  const handleEntryChange = (index, field, value) => {
    const newEntries = [...agendaData.entries];
    newEntries[index] = { ...newEntries[index], [field]: value };
    setAgendaData({ ...agendaData, entries: newEntries });
  };

  const handleCopyPreviousClass = () => {
    if (activeTab > 0) {
      const previousEntry = agendaData.entries[activeTab - 1];
      const newEntries = [...agendaData.entries];
      newEntries[activeTab] = {
        ...newEntries[activeTab],
        disciplina: previousEntry.disciplina,
        conteudo: previousEntry.conteudo,
        atividade: previousEntry.atividade,
        casa: previousEntry.casa,
      };
      setAgendaData({ ...agendaData, entries: newEntries });
    }
  };

  const handleDateChange = (e) => {
    setAgendaData({ ...agendaData, date: e.target.value });
  };

  const saveLastAgenda = () => {
    try {
      localStorage.setItem(
        LAST_AGENDA_STORAGE_KEY,
        JSON.stringify({
          entries: normalizeEntries(agendaData.entries),
          savedAt: new Date().toISOString(),
        })
      );
      setHasLastAgenda(true);
    } catch (error) {
      console.error("Erro ao salvar rascunho da agenda:", error);
    }
  };

  const handleLoadLastAgenda = () => {
    try {
      const savedAgenda = JSON.parse(localStorage.getItem(LAST_AGENDA_STORAGE_KEY) || "null");
      if (!savedAgenda?.entries) return;
      setAgendaData((current) => ({
        ...current,
        entries: normalizeEntries(savedAgenda.entries),
      }));
      setActiveTab(0);
    } catch (error) {
      console.error("Erro ao carregar última agenda:", error);
    }
  };

  const handleClearAgenda = () => {
    setAgendaData((current) => ({
      ...current,
      entries: createEmptyEntries(),
    }));
    setActiveTab(0);
  };

  const handleGenerateImage = async () => {
    try {
      const preview = document.getElementById("agenda-preview");
      if (!preview) return;

      // Aguardar mais tempo para garantir que tudo esteja totalmente renderizado
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await html2canvas(preview, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: null,
        logging: false,
        width: preview.offsetWidth,
        height: preview.offsetHeight,
        removeContainer: false,
        imageTimeout: 15000,
        foreignObjectRendering: false, // Desabilitar foreignObject para melhor compatibilidade
        onclone: (clonedDoc) => {
          // Garantir que os elementos estejam visíveis no clone
          const clonedPreview = clonedDoc.getElementById("agenda-preview");
          if (clonedPreview) {
            // Garantir que o container principal tenha as dimensões corretas
            clonedPreview.style.width = "595px";
            clonedPreview.style.height = "842px";
            
            // Procurar o div de conteúdo de forma mais precisa
            // Procurar pelo div que tem backgroundColor rgba(255, 255, 255, 0.95)
            const allDivs = clonedPreview.querySelectorAll("div");
            let contentDiv = null;
            for (const div of allDivs) {
              const bgColor = div.style.backgroundColor || "";
              if (bgColor.includes("255, 255, 255") || bgColor.includes("rgba(255, 255, 255")) {
                contentDiv = div;
                break;
              }
            }
            // Fallback: usar o primeiro div com position absolute
            if (!contentDiv) {
              contentDiv = clonedPreview.querySelector("div[style*='position: absolute']");
            }
            
            if (contentDiv) {
              // Aplicar EXATAMENTE os mesmos estilos do preview
              contentDiv.style.position = "absolute";
              contentDiv.style.top = "3%";
              contentDiv.style.left = "5%";
              contentDiv.style.right = "5%";
              contentDiv.style.width = "auto";
              contentDiv.style.bottom = "20%";
              contentDiv.style.backgroundColor = "rgba(255, 255, 255, 0.95)";
              contentDiv.style.padding = "8px";
              contentDiv.style.borderRadius = "4px";
              contentDiv.style.paddingBottom = "12px";
              contentDiv.style.zIndex = "10";
              contentDiv.style.visibility = "visible";
              contentDiv.style.opacity = "1";
              // Remover overflow durante captura para evitar problemas
              contentDiv.style.overflowY = "visible";
              contentDiv.style.overflow = "visible";
              
              // Preservar estilos de grid - procurar elementos com gridTemplateColumns inline
              const allElements = contentDiv.querySelectorAll("*");
              allElements.forEach((el) => {
                el.style.visibility = "visible";
                el.style.opacity = "1";
                // Se o elemento já tem gridTemplateColumns no style inline, preservar
                if (el.style.gridTemplateColumns) {
                  el.style.display = "grid";
                  // Não alterar gridTemplateColumns, já está definido
                } else if (el.style.display === "grid") {
                  // Já é grid, preservar
                } else if (el.style.display === "flex") {
                  // Já é flex, preservar
                } else if (!el.style.display) {
                  // Apenas definir display se não tiver
                  el.style.display = "block";
                }
                // Não sobrescrever display se já estiver definido
              });
            }
            // Garantir que a imagem de fundo seja carregada
            const bgImage = clonedPreview.querySelector("img");
            if (bgImage) {
              bgImage.style.zIndex = "1";
              bgImage.style.display = "block";
              bgImage.style.width = "100%";
              bgImage.style.height = "100%";
            }
          }
        },
      });

      canvas.toBlob(
        (blob) => {
          const imageFile = new File(
            [blob],
            `agenda-${agendaData.date || "sem-data"}.png`,
            { type: "image/png" }
          );
          if (onImageGenerate) {
            onImageGenerate(imageFile, agendaData.date);
          }
          if (hasFilledEntry(agendaData.entries)) {
            saveLastAgenda();
          }
        },
        "image/png",
        1.0
      );
      setShowPreview(false);
    } catch (error) {
      console.error("Erro ao gerar imagem:", error);
      alert("Erro ao gerar imagem. Por favor, tente novamente.");
    }
  };

  const PreviewContent = () => (
    <div
      id="agenda-preview"
      style={{
        position: "relative",
        width: "595px",
        height: "842px",
        margin: "0 auto",
        backgroundColor: "#ffffff",
      }}
    >
      <img
        src={backgroundImage}
        alt="Background"
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 1, // Fundo com z-index menor
        }}
      />

      <div
        style={{
          position: "absolute",
          top: "3%", // Diminuir o top para subir o conteúdo e não cortar
          left: "5%", // Margem esquerda
          right: "5%", // Margem direita - isso garante que o conteúdo ocupe 90% da largura
          width: "auto", // Usar auto quando temos left e right
          bottom: "20%", // Garantir espaço na parte inferior para os elementos do rodapé
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          padding: "8px",
          borderRadius: "4px",
          paddingBottom: "12px",
          zIndex: 10, // Garantir que o conteúdo fique acima da imagem de fundo
          overflowY: "auto", // Permitir scroll se necessário
        }}
      >
        <Typography
          align="center"
          style={{ fontWeight: "bold", fontSize: "0.85rem", marginBottom: "3px" }}
        >
          AGENDA DIÁRIA -{" "}
          {new Date(agendaData.date + "T00:00:00").toLocaleDateString("pt-BR")}
        </Typography>

        <Box style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
          {agendaData.entries
            .filter(
              (entry) =>
                entry.disciplina ||
                entry.conteudo ||
                entry.atividade ||
                entry.casa
            )
            .map((entry, index) => (
              <Paper
                key={index}
                elevation={1}
                style={{
                  padding: "6px",
                  marginBottom: "3px",
                  borderRadius: "4px",
                  backgroundColor: "#f5f5f5",
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "3px",
                  }}
                >
                  <Typography style={{ fontWeight: "bold", fontSize: "0.75rem" }}>
                    {entry.aula}
                  </Typography>
                  <Typography style={{ color: "#1976d2", fontSize: "0.75rem" }}>
                    {entry.disciplina}
                  </Typography>
                </Box>

                {/* Layout em 2 colunas para economizar espaço vertical */}
                <Box
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr", // 2 colunas
                    gap: "4px",
                    fontSize: "0.7rem",
                  }}
                >
                  {/* Coluna esquerda */}
                  <Box style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                    <Box>
                      <Typography
                        style={{
                          fontWeight: 500,
                          color: "#666",
                          fontSize: "0.7rem",
                          marginBottom: "2px",
                        }}
                      >
                        Conteúdo:
                      </Typography>
                      <Typography style={{ fontSize: "0.7rem", lineHeight: 1.2 }}>
                        {entry.conteudo || "-"}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Coluna direita */}
                  <Box style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                    <Box>
                      <Typography
                        style={{
                          fontWeight: 500,
                          color: "#666",
                          fontSize: "0.7rem",
                          marginBottom: "2px",
                        }}
                      >
                        Atividade em Sala:
                      </Typography>
                      <Typography style={{ fontSize: "0.7rem", lineHeight: 1.2 }}>
                        {entry.atividade || "-"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {/* Atividade para Casa em linha completa (se houver) */}
                {entry.casa && (
                  <Box style={{ marginTop: "3px", paddingTop: "3px", borderTop: "1px solid #e0e0e0" }}>
                    <Typography
                      style={{
                        fontWeight: 500,
                        color: "#666",
                        fontSize: "0.7rem",
                        marginBottom: "2px",
                      }}
                    >
                      Atividade para Casa:
                    </Typography>
                    <Typography style={{ fontSize: "0.7rem", lineHeight: 1.2 }}>
                      {entry.casa}
                    </Typography>
                  </Box>
                )}
              </Paper>
            ))}
        </Box>
      </div>
    </div>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <StyledPaper elevation={2}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <Box display="flex" alignItems="center" gap={1}>
              <SchoolIcon color="primary" sx={{ fontSize: 20 }} />
              <TextField
                type="date"
                size="small"
                value={agendaData.date}
                onChange={handleDateChange}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <DateRangeIcon
                      color="action"
                      sx={{ mr: 1, fontSize: 20 }}
                    />
                  ),
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={8}>
            <Stepper
              activeStep={activeTab}
              alternativeLabel
              sx={{ minHeight: "auto" }}
            >
              {agendaData.entries.map((entry, index) => (
                <Step key={index}>
                  <StepLabel
                    sx={{
                      "& .MuiStepLabel-label": { fontSize: "0.75rem" },
                      "& .MuiSvgIcon-root": { fontSize: 20 },
                    }}
                  >
                    {entry.aula}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>
        </Grid>
      </StyledPaper>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RestoreIcon />}
            onClick={handleLoadLastAgenda}
            disabled={!hasLastAgenda}
          >
            Usar última agenda
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            startIcon={<DeleteSweepIcon />}
            onClick={handleClearAgenda}
            disabled={!hasFilledEntry(agendaData.entries)}
          >
            Limpar campos
          </Button>
        </Stack>
      </Box>

      <StyledPaper>
        <Box mb={2}>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {agendaData.entries.map((_, index) => (
              <StyledTab key={index} label={`${index + 1}ª Aula`} />
            ))}
          </Tabs>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            {activeTab > 0 && (
              <Box display="flex" justifyContent="flex-end" mb={1}>
                <Tooltip title="Copiar dados da aula anterior">
                  <IconButton
                    onClick={handleCopyPreviousClass}
                    color="primary"
                    size="small"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Disciplina"
              value={agendaData.entries[activeTab].disciplina}
              onChange={(e) =>
                handleEntryChange(activeTab, "disciplina", e.target.value)
              }
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Conteúdo"
              value={agendaData.entries[activeTab].conteudo}
              onChange={(e) =>
                handleEntryChange(activeTab, "conteudo", e.target.value)
              }
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Atividade em Sala"
              value={agendaData.entries[activeTab].atividade}
              onChange={(e) =>
                handleEntryChange(activeTab, "atividade", e.target.value)
              }
              variant="outlined"
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Atividade para Casa"
              value={agendaData.entries[activeTab].casa}
              onChange={(e) =>
                handleEntryChange(activeTab, "casa", e.target.value)
              }
              variant="outlined"
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </StyledPaper>

      <Box display="flex" justifyContent="center" gap={2} my={2}>
        <Button
          variant="outlined"
          size="medium"
          startIcon={<VisibilityIcon />}
          onClick={() => setShowPreview(true)}
        >
          Visualizar
        </Button>
      </Box>

      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth={false}
        PaperProps={{
          style: {
            width: "595px",
            height: "auto",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            m: 0,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Preview da Agenda
          <IconButton onClick={() => setShowPreview(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <PreviewContent />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Fechar</Button>
          <Button
            onClick={handleGenerateImage}
            variant="contained"
            startIcon={<SaveAltIcon />}
          >
            Gerar Imagem
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AgendaForm;
